import React, { useEffect, useMemo, useState } from 'react';
import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import Dialog from 'Components/UI/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import useMobileLayout from 'Hooks/useMobileLayout';
import { Paper } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import Tooltip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Tooltip';
import fr from 'facet-rest';
import { SimpleActionItemDraftOverride } from 'Resources/SimpleActionItem';
import DeleteIcon from '@material-ui/icons/Delete';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { IconButton, makeStyles } from '@material-ui/core';
import Button from 'Components/UI/Button';
import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';
import Typography from 'Components/UI/Typography';
import {
  getPriorityConfig,
} from 'Components/ComponentLibrary/Organisms/MessageList/MessageListItem/MessageContent/Content/MessagesActionItemCard';
import { useTheme } from '@material-ui/core/styles';
import {
  addQuery, addQueryWithReplace, QueryVal, removeQuery,
} from 'Helpers/Query';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Skeleton } from '@material-ui/lab';
import { dispatchActionItemRefresh } from 'Hooks/useRefreshEvent';

const useStyles = makeStyles((theme) => ({
  sidebar: {
    backgroundColor: theme.palette.facet.lightGray,
    padding: theme.spacing(1, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 350,
  },
  modalContent: {
    display: 'flex',
  },
  draftListItemRoot: {
    margin: theme.spacing(1, 0),
    width: '100%',
  },
  selected: {
    backgroundColor: theme.palette.facet.primaryBlueMuted2,
  },
  white: {
    color: 'white',
  },
  paper: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    cursor: 'pointer',
  },
  draftName: {
    flexGrow: 1,
  },
  chip: {
    pointerEvents: 'none',
    borderRadius: 10,
    marginRight: theme.spacing(2),
    color: 'white',
    fontWeight: theme.typography.fontWeightFacetBold,
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
  },
  addMoreButton: {
    marginTop: theme.spacing(1),
  },
  bottomBar: {
    alignItems: 'center',
    borderTop: `solid 1px ${theme.palette.facet.border}`,
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  metaForm: {
    flexGrow: 1,
  },
  skeleton: {
    margin: theme.spacing(1),
  },
  skeletonLine: {
    margin: theme.spacing(1, 2),
  },
  skeletonDualGroup: {
    display: 'flex',
  },
  skeletonDualLine: {
    flexGrow: 1,
    margin: theme.spacing(1, 2),
  },
  warning: {
    marginRight: theme.spacing(1),
  },
}));

function ActionItemDraftList({
  draftCollection,
  loading,
  activeDraftResourceSelfLink,
  handleDelete,
  handleSelect,
}) {
  const classes = useStyles();

  const theme = useTheme();
  if (loading) {
    return Array(3)
      .fill()
      .map(() => (
        <Skeleton animation="wave" variant="rect" width={318} height={64} className={classes.skeleton} />
      ));
  }
  return !!draftCollection && draftCollection.resources().list().map(el => {
    const { color, label } = getPriorityConfig(theme, el.attr('priority'));
    const selected = el?.link('self')?.url() && el.link('self')?.url() === activeDraftResourceSelfLink;
    return (
      <div className={classes.draftListItemRoot} key={`draft-list-item-${el.id()}`}>
        <Paper
          className={classNames(classes.paper, {
            [classes.selected]: selected,
          })}
          variant="tight"
          onClick={() => handleSelect(el)}
          test="action-item-draft-list-item"
        >
          {label && <Chip className={classes.chip} label={label} style={{ backgroundColor: color }} />}
          <Tooltip title={el.attr('name') || '[Unnamed]'} arrow>
            <Typography variant="h5" color={selected ? 'white' : 'primaryGray'} weight="medium" noWrap className={classes.draftName}>
              {el.attr('name') || '[Unnamed]'}
            </Typography>
          </Tooltip>
          <IconButton
            className={classNames(classes.deleteButton, {
              [classes.white]: selected,
            })}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(el);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Paper>
      </div>
    );
  });
}

function SimpleActionItemGroupModal({ defaultValues }) {
  const classes = useStyles();
  const { householdResource } = useSimpleHousehold();
  const { collection: draftCollection, setCollection: setDraftCollection } = useCollection();
  const [draftGroupLoading, setDraftGroupLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirtyForm, setDirtyForm] = useState(false);
  const [activeDraftResource, setActiveDraftResource] = useState(null);
  const [defaultValuesName, setDefaultValuesName] = useState(defaultValues.name);
  const isMobile = useMobileLayout();

  const activeDraftResourceSelfLink = QueryVal('actionItemDraftResourceSelfLink');

  const override = useMemo(() => {
    let metaOverride = SimpleActionItemDraftOverride;
    Object.keys(defaultValues).forEach((key) => {
      const defaultValue = defaultValues[key];
      if (key === 'assignedTo') {
        if (defaultValues[key] === 'Household') {
          let defaultOptionValue = '';
          metaOverride = metaOverride.nameOverride(key, (v) => {
            const options = v.options.filter(option => option.group === 'Household');
            if (options.length > 0) {
              defaultOptionValue = options[0].id.toString();
            }
            return { default: defaultOptionValue };
          });
        } else if (defaultValues[key] === 'Me') {
          let defaultOptionValue = '';
          metaOverride = metaOverride.nameOverride(key, (v) => {
            const options = v.options.filter(option => option.group === 'Me');
            if (options.length > 0) {
              defaultOptionValue = options[0].id.toString();
            }
            return { default: defaultOptionValue };
          });
        } else {
          metaOverride = metaOverride.nameOverride(key, { default: defaultValues[key] });
        }
      } else {
        metaOverride = metaOverride.nameOverride(key, { default: defaultValue });
      }
    });
    return metaOverride;
  }, [defaultValues]);

  const refreshActionItemDraftGroup = async () => {
    if (!householdResource?.relationship(fr.relationships.actionItemDraftGroup.name)) {
      return;
    }

    try {
      setDraftGroupLoading(true);
      const [actionItemDraftGroupResource] = await householdResource.relationship(fr.relationships.actionItemDraftGroup.name).link('related').get();
      const coll = actionItemDraftGroupResource.attr('actionItemDrafts');
      if (coll.resources().length() === 0 || defaultValuesName) {
        const newResource = actionItemDraftGroupResource.attr('actionItemDrafts').create({
          metaOverrideSet: override,
        });
        setActiveDraftResource(newResource);
      } else if (!activeDraftResourceSelfLink) {
        addQuery({ actionItemDraftResourceSelfLink: coll.resources().head()?.link('self')?.url() });
      }
      setDraftCollection(coll);
    } catch (err) {
      displayErrorMessage(err, err.errors || 'Failed to open modal');
    } finally {
      setDraftGroupLoading(false);
    }
  };

  const saveDraftResource = async (draftResource) => {
    await draftResource.submit();
    setDirtyForm(false);
    setDefaultValuesName(null);
  };

  const selectDraftResource = async (draftResource) => {
    if (activeDraftResource?.isValid()) {
      await saveDraftResource(activeDraftResource);
    }
    if (draftResource?.link('self')?.url()) {
      addQuery({ actionItemDraftResourceSelfLink: draftResource.link('self').url() });
    }
  };

  const validateResources = () => {
    if (!draftCollection) {
      return null;
    }
    for (const el of draftCollection.resources().list()) {
      if (!el.isValid()) {
        return false;
      }
    }
    return true;
  };

  const addDraftResource = async () => {
    if (activeDraftResource?.isValid()) {
      await saveDraftResource(activeDraftResource);
    }

    removeQuery('actionItemDraftResourceSelfLink');
    setActiveDraftResource(draftCollection.create());
  };

  const deleteDraftResource = async (draftResource) => {
    await draftResource.delete();
    if (draftResource?.link('self').url() === activeDraftResourceSelfLink) {
      removeQuery('actionItemDraftResourceSelfLink');
    }
    await refreshActionItemDraftGroup();
  };

  const publishDraftGroup = async () => {
    if (!householdResource?.relationship(fr.relationships.actionItemDraftGroup.name)) {
      return;
    }
    try {
      const [actionItemDraftGroupResource] = await householdResource.relationship(fr.relationships.actionItemDraftGroup.name).link('related').get();
      await actionItemDraftGroupResource.submit();
      removeQuery('actionItemDraftResourceSelfLink');
    } catch (err) {
      displayErrorMessage(err, err.errors || 'Failed to create tasks');
    }
  };

  const handleModalClose = () => {
    if (!validateResources()) {
      return;
    }
    removeQuery('modal');
  };

  const handlePublish = async () => {
    handleModalClose();

    setPublishing(true);
    await publishDraftGroup();

    setPublishing(false);
    displaySuccess('Created Tasks');
    addQuery({ refreshSimpleActionItems: 'true' });
    addQueryWithReplace({ refresh: 'action-item' });
    dispatchActionItemRefresh();
  };

  useEffect(() => {
    if (!householdResource?.relationship(fr.relationships.actionItemDraftGroup.name)) {
      return;
    }

    refreshActionItemDraftGroup();
  }, [householdResource]);

  useEffect(() => {
    if (activeDraftResource?.link('self')?.url() === activeDraftResourceSelfLink) {
      return;
    }
    if (!draftCollection) {
      return;
    }

    if (activeDraftResourceSelfLink && !defaultValuesName) {
      draftCollection.resources().list().forEach(el => {
        if (el?.link('self')?.url() && el.link('self').url() === activeDraftResourceSelfLink) {
          setActiveDraftResource(el);
        }
      });
    }
  }, [activeDraftResourceSelfLink, draftCollection]);

  let householdText = '';
  if (householdResource) {
    householdText = `${householdResource.attr('primaryClientFirstName')} ${householdResource.attr('primaryClientLastName')} [${householdResource.id()}]`;
  }

  // All saved resources are valid, no forms are dirty, and the selected resource has a self link (which means it has been saved)
  const allAreValid = useMemo(() => (!dirtyForm && activeDraftResource?.link('self') && draftCollection?.resources().list().every(el => el.isValid())), [draftCollection, activeDraftResource, dirtyForm]);

  const metaFormSkeleton = (
    <div className={classes.metaForm}>
      <Skeleton animation="wave" variant="text" height={32} className={classes.skeletonLine} />
      <span className={classes.skeletonDualGroup}>
        <Skeleton animation="wave" variant="text" height={32} className={classes.skeletonDualLine} />
        <Skeleton animation="wave" variant="text" height={32} className={classes.skeletonDualLine} />
      </span>
      <span className={classes.skeletonDualGroup}>
        <Skeleton animation="wave" variant="text" height={32} className={classes.skeletonDualLine} />
        <Skeleton animation="wave" variant="text" height={32} className={classes.skeletonDualLine} />
      </span>
      <span className={classes.skeletonDualGroup}>
        <Skeleton animation="wave" variant="text" height={32} className={classes.skeletonDualLine} />
        <Skeleton animation="wave" variant="text" height={32} className={classes.skeletonDualLine} />
      </span>
      <Skeleton animation="wave" variant="rect" height={400} className={classNames(classes.metaForm, classes.skeletonLine)} />
    </div>
  );

  let formRows = [
    ['name'],
    ['dueDate', 'priority'],
    ['status', 'timeToComplete'],
    ['assignedTo', 'showOnBlueprint'],
    ['pillar'],
    ['description'],
  ];

  if (defaultValues?.isParaplanner) {
    formRows = [
      ['name'],
      ['dueDate', 'priority'],
      ['status'],
      ['assignedTo'],
      ['description'],
    ];
  }

  return (
    <React.Fragment>
      <Dialog
        open
        fullScreen={isMobile}
        onClose={handleModalClose}
        maxWidth="lg"
        fullWidth
        test={'simple-action-item-dialog'}
      >
        <DialogTitle>
          {`Add tasks for ${householdText}`}
        </DialogTitle>
        <div className={classes.modalContent}>
          {draftGroupLoading
            ? metaFormSkeleton
            : (
              <React.Fragment>
                {activeDraftResource && (
                  <MetaFormInline
                    className={classes.metaForm}
                    resource={activeDraftResource}
                    metaOverrideSet={override}
                    rows={formRows}
                    onChange={() => setDirtyForm(true)}
                    onSubmit={async (res) => {
                      setDirtyForm(false);
                      const createdDraft = res.resources().head();
                      setDefaultValuesName(null);
                      await refreshActionItemDraftGroup();
                      addQuery({ actionItemDraftResourceSelfLink: createdDraft.link('self').url() });
                    }}
                    submitLabelOverride={'Save Draft'}
                    SubmitButtonProps={{
                      test: 'simple-action-item-dialog-save-draft-button',
                    }}
                  />
                )}
              </React.Fragment>
            )}
          <Paper className={classes.sidebar}>
            <ActionItemDraftList
              draftCollection={draftCollection}
              loading={draftGroupLoading}
              activeDraftResourceSelfLink={activeDraftResourceSelfLink}
              handleDelete={deleteDraftResource}
              handleSelect={selectDraftResource}
            />
            <Button
              test={'tasks-modal-add-more-tasks'}
              id="add-more-tasks"
              color="secondary"
              variant="contained"
              onClick={addDraftResource}
              className={classes.addMoreButton}
              disabled={!activeDraftResource?.link('self')}
            >
              {'Add More Tasks'}
            </Button>
          </Paper>
        </div>
        <div className={classes.bottomBar}>
          <Typography variant="h6" color="red" weight="default" className={classes.warning}><em>{allAreValid ? '' : 'All drafts must be saved before publishing.'}</em></Typography>
          <Button
            id="publish-drafts-button"
            color="primary"
            variant="contained"
            onClick={handlePublish}
            disabled={!allAreValid || publishing}
            test={'simple-action-item-dialog-submit-button'}
          >
            {publishing ? 'Sending...' : 'Create Tasks'}
          </Button>
        </div>
      </Dialog>
    </React.Fragment>
  );
}

SimpleActionItemGroupModal.defaultProps = {
  defaultValues: {},
};

SimpleActionItemGroupModal.propTypes = {
  defaultValues: PropTypes.object,
};

export default SimpleActionItemGroupModal;
